import React, { createContext, useContext, useState, useEffect } from 'react'
import { getConsent } from '../../../services/projectService'
import { jsPDF } from 'jspdf'

type SignatureData = {
    workerId: number
    signatureURL: string // data URL
    points: Array<{ points: number[] }>

}

type ConsentContextType = {
    consentTexts: Record<string, string>
    signatures: SignatureData[]
    addSignature: (workerId: number, signature: string, lines: Array<{ points: number[] }>) => void
    generateConsentForms: (workerIds: number[]) => Array<{ workerId: number; blob: Blob }>
    getSignature: (workerId: number) => Array<{ points: number[] }> | undefined
    loadConsentTexts: () => Promise<void>
    clearSignatures: () => void
    removeSignature: (workerId: number) => void
}

const ConsentContext = createContext<ConsentContextType | null>(null)

export const useConsentContext = () => {
    const context = useContext(ConsentContext)
    if (!context) {
        throw new Error('useConsentContext must be used within a ConsentProvider')
    }
    return context
}

export const ConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [consentTexts, setConsentTexts] = useState<Record<string, string>>({})
    const [signatures, setSignatures] = useState<SignatureData[]>([])

    const loadConsentTexts = async () => {
        const response = await getConsent()
        setConsentTexts(response)
    }

    const addSignature = (workerId: number, signature: string, lines: Array<{ points: number[] }>) => {
        setSignatures(prev => {
            // Remove any existing signature for this worker
            const filtered = prev.filter(s => s.workerId !== workerId)
            // Add the new signature
            return [...filtered, { workerId, signatureURL: signature, points: lines }]
        })
    }

    const removeSignature = (workerId: number) => {
        setSignatures(prev => prev.filter(s => s.workerId !== workerId))
    }

    const getSignature = (workerId: number) => {
        return signatures.find(s => s.workerId === workerId)?.points
    }

    const clearSignatures = () => {
        setSignatures([])
    }

    const generateConsentForms = (workerIds: number[]): Array<{ workerId: number; blob: Blob }> => {
        // Only generate forms for workers who have signatures
        const workersWithSignatures = workerIds.filter(id => {
            const signature = getSignature(id)
            return signature
        })
        if (workersWithSignatures.length === 0) return []

        return workersWithSignatures.map(workerId => {
            const pdf = new jsPDF('p', 'px', 'a4')
            const workerSignature = signatures.find(s => s.workerId === workerId)?.signatureURL
            if (workerSignature) {
                // Add signature image with compression
                pdf.addImage(workerSignature, 'PNG', 20, 20, 400, 210, undefined, 'FAST')
            }

            return {
                workerId,
                blob: pdf.output('blob')
            }
        })
    }

    return (
        <ConsentContext.Provider value={{ 
            consentTexts, 
            signatures,
            addSignature,
            removeSignature,
            generateConsentForms,
            getSignature,
            loadConsentTexts,
            clearSignatures 
        }}>
            {children}
        </ConsentContext.Provider>
    )
} 